import { Button, Chip, Typography } from '@/components';
import { cn } from '@/utils/styles';

type FeatureItem = string | { text: string; subFeatures: string[] };

type PricingCardProps = {
  plan: 'basic' | 'premium' | 'enterprise';
  price?: string;
  features?: FeatureItem[];
  isActive?: boolean;
  title?: string;
  explanation?: string;
  onSelect?: (plan: 'basic' | 'premium' | 'enterprise') => void;
  isSelectable?: boolean;
  buttonText: string;
};

const titles = {
  basic: 'Basic',
  premium: 'Premium',
  enterprise: 'Enterprise',
};

export const PricingCardSeller = ({
  plan,
  price,
  features,
  isActive,
  title,
  explanation,
  onSelect,
  isSelectable,
  buttonText,
}: PricingCardProps) => {
  const isEnterprise = plan === 'enterprise';

  return (
    <div
      className={cn(
        'relative rounded-lg border p-6 text-center shadow-md transition duration-200',
        isActive ? 'ring-3 ring-green-700' : 'border-gray-200 bg-white'
      )}
    >
      {isActive && (
        <div className="absolute -top-4 left-1/2 w-full -translate-x-1/2">
          <Chip className="bg-green-700 px-2 text-white">
            <span>Your Current Plan</span>
          </Chip>
        </div>
      )}
      <Typography as="h6" weight="semibold" className="mb-2 uppercase" size="title">
        {titles[plan]}
      </Typography>

      <div className="mb-2 text-left">
        <Typography weight="light" size="body-3" className="mb-1 italic">
          {explanation}
        </Typography>
      </div>

      {price && (
        <Typography weight="semibold" size="heading-2" className="mb-4">
          {price.split('+')[0].trim()}
          <span className="ml-1 text-sm font-medium text-gray-600">€ + VAT</span>
        </Typography>
      )}

      {onSelect && isSelectable && (
        <Button onClick={() => onSelect(plan)} className="w-full">
          {buttonText}
        </Button>
      )}

      <hr className="mt-4" />

      {title && (
        <Typography weight="medium" size="body-2" className="mt-4">
          {title}:
        </Typography>
      )}

      <ul className="mt-2 list-disc pl-3 text-black">
        {features?.map((item, index) => {
          if (typeof item === 'string') {
            return (
              <li key={index} className="text-left">
                <Typography color="primary" weight="normal" size="body-3">
                  {item}
                </Typography>
              </li>
            );
          } else {
            return (
              <li key={index} className="text-left">
                <Typography color="primary" weight="normal" size="body-3">
                  {item.text}
                </Typography>
                <ul className="mt-1 pl-5 [&>li]:list-[circle]">
                  {item.subFeatures.map((subFeature, subIndex) => (
                    <li key={subIndex} className="text-left">
                      <Typography color="primary" weight="normal" size="body-3">
                        {subFeature}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </li>
            );
          }
        })}
      </ul>
    </div>
  );
};
